import React, { useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import { ReactComponent as ChevronDown } from "@assets/Icons/IconChevron-Down.svg";
import { ReactComponent as RefreshIcon } from "@assets/Icons/IconRefreshV2.svg";

export interface TimeRange {
  start: Date;
  end: Date;
}

export type PresetKey =
  | "15m"
  | "1h"
  | "6h"
  | "24h"
  | "today"
  | "yesterday"
  | "7d";

export type TimeRangeMode =
  | { kind: "preset"; key: PresetKey }
  | { kind: "custom" };

interface Preset {
  key: PresetKey;
  label: string;
  resolve: () => TimeRange;
}

const minutesAgo = (mins: number): TimeRange => {
  const end = new Date();
  return { start: new Date(end.getTime() - mins * 60_000), end };
};

const startOfToday = (): TimeRange => {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  return { start, end: new Date() };
};

const yesterdayRange = (): TimeRange => {
  const start = new Date();
  start.setDate(start.getDate() - 1);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setHours(23, 59, 59, 999);
  return { start, end };
};

export const PRESETS: Preset[] = [
  { key: "15m", label: "Last 15 minutes", resolve: () => minutesAgo(15) },
  { key: "1h", label: "Last 1 hour", resolve: () => minutesAgo(60) },
  { key: "6h", label: "Last 6 hours", resolve: () => minutesAgo(360) },
  { key: "24h", label: "Last 24 hours", resolve: () => minutesAgo(1440) },
  { key: "today", label: "Today", resolve: startOfToday },
  { key: "yesterday", label: "Yesterday", resolve: yesterdayRange },
  { key: "7d", label: "Last 7 days", resolve: () => minutesAgo(7 * 24 * 60) },
];

export const DEFAULT_PRESET: PresetKey = "1h";

export const resolvePreset = (key: PresetKey): TimeRange => {
  const preset = PRESETS.find((p) => p.key === key);
  return preset ? preset.resolve() : minutesAgo(60);
};

const pad = (n: number) => String(n).padStart(2, "0");

const toDateTimeLocal = (d: Date): string =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;

interface Props {
  value: TimeRange;
  mode: TimeRangeMode;
  onChange: (range: TimeRange, mode: TimeRangeMode) => void;
  onRefresh?: () => void;
  isLoading?: boolean;
}

const LogsTimeRangePicker: React.FC<Props> = ({
  value,
  mode,
  onChange,
  onRefresh,
  isLoading = false,
}) => {
  const [open, setOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [draftStart, setDraftStart] = useState(toDateTimeLocal(value.start));
  const [draftEnd, setDraftEnd] = useState(toDateTimeLocal(value.end));
  const [error, setError] = useState<string | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Sync drafts when external range changes (e.g. histogram bucket click).
  useEffect(() => {
    setDraftStart(toDateTimeLocal(value.start));
    setDraftEnd(toDateTimeLocal(value.end));
  }, [value.start, value.end]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
        setError(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const triggerLabel = useMemo(() => {
    if (mode.kind === "preset") {
      return PRESETS.find((p) => p.key === mode.key)?.label ?? "Custom range";
    }
    return `${format(value.start, "dd/MM HH:mm")} → ${format(
      value.end,
      "dd/MM HH:mm"
    )}`;
  }, [mode, value]);

  const openMenu = () => {
    setShowCustom(mode.kind === "custom");
    setError(null);
    setOpen(true);
  };

  const applyPreset = (preset: Preset) => {
    onChange(preset.resolve(), { kind: "preset", key: preset.key });
    setOpen(false);
    setShowCustom(false);
    setError(null);
  };

  const applyCustom = () => {
    const start = new Date(draftStart);
    const end = new Date(draftEnd);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      setError("Enter a valid date and time.");
      return;
    }
    if (start >= end) {
      setError("Start must be before end.");
      return;
    }
    onChange({ start, end }, { kind: "custom" });
    setOpen(false);
    setShowCustom(false);
    setError(null);
  };

  return (
    <div className="flex items-center gap-2" ref={wrapRef}>
      <div className="relative">
        <button
          type="button"
          onClick={() => (open ? setOpen(false) : openMenu())}
          className="h-8 px-3 flex items-center gap-2 rounded-sm border border-semantic-border-secondary bg-semantic-bg-primary-alt text-sm text-semantic-text-secondary hover:border-semantic-border-brand-secondary"
        >
          <span>{triggerLabel}</span>
          <ChevronDown className="w-3 h-3 flex-shrink-0" />
        </button>

        {open && (
          <div className="absolute z-50 mt-1 left-0 bg-semantic-bg-primary border border-semantic-border-primary shadow-lg rounded-md min-w-[260px] p-1">
            {!showCustom ? (
              <>
                {PRESETS.map((p) => {
                  const active =
                    mode.kind === "preset" && mode.key === p.key;
                  return (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => applyPreset(p)}
                      className={`w-full text-left px-3 py-1.5 text-sm rounded-sm hover:bg-semantic-state-active ${
                        active
                          ? "bg-semantic-state-active text-semantic-text-primary font-semibold"
                          : "text-semantic-text-primary"
                      }`}
                    >
                      {p.label}
                    </button>
                  );
                })}
                <div className="border-t border-semantic-border-primary my-1" />
                <button
                  type="button"
                  onClick={() => {
                    setShowCustom(true);
                    setError(null);
                  }}
                  className={`w-full text-left px-3 py-1.5 text-sm rounded-sm hover:bg-semantic-state-active ${
                    mode.kind === "custom"
                      ? "bg-semantic-state-active text-semantic-text-primary font-semibold"
                      : "text-semantic-text-primary"
                  }`}
                >
                  Custom range…
                </button>
              </>
            ) : (
              <div className="p-3 flex flex-col gap-3 min-w-[300px]">
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-semantic-text-secondary">
                    From
                  </label>
                  <input
                    type="datetime-local"
                    value={draftStart}
                    onChange={(e) => setDraftStart(e.target.value)}
                    className="h-8 px-2 rounded-sm border border-semantic-border-secondary bg-semantic-bg-primary-alt text-sm text-semantic-text-primary focus:outline-none focus:ring-2 focus:ring-semantic-text-brand"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-semantic-text-secondary">
                    To
                  </label>
                  <input
                    type="datetime-local"
                    value={draftEnd}
                    onChange={(e) => setDraftEnd(e.target.value)}
                    className="h-8 px-2 rounded-sm border border-semantic-border-secondary bg-semantic-bg-primary-alt text-sm text-semantic-text-primary focus:outline-none focus:ring-2 focus:ring-semantic-text-brand"
                  />
                </div>
                {error && (
                  <div className="text-xs text-semantic-text-danger">
                    {error}
                  </div>
                )}
                <div className="flex justify-between items-center pt-1">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCustom(false);
                      setError(null);
                    }}
                    className="text-xs text-semantic-text-secondary hover:underline"
                  >
                    ← Presets
                  </button>
                  <button
                    type="button"
                    onClick={applyCustom}
                    className="h-8 px-3 rounded-sm bg-semantic-bg-brand-bold-secondary text-white text-sm font-semibold hover:opacity-90"
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {onRefresh && (
        <button
          type="button"
          onClick={onRefresh}
          disabled={isLoading}
          aria-label="Refresh"
          title="Refresh"
          className="h-8 w-8 flex items-center justify-center rounded-sm border border-semantic-border-secondary bg-semantic-bg-primary-alt text-semantic-text-secondary hover:border-semantic-border-brand-secondary disabled:opacity-50"
        >
          <RefreshIcon
            className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`}
          />
        </button>
      )}
    </div>
  );
};

export default LogsTimeRangePicker;
